import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Modal, ToggleSwitch } from 'flowbite-react';
import { Quote } from '../../models';
import QuotesApi from '../../services/Quotes';
import { Button } from '..';
import {
    defaultQuoteRecipientEmail,
    quoteRecipientOptions,
} from '../../utils/quoteSendRecipients';
import { roleBadgeColor, roleLabelKey } from '../../utils/clientContacts';
import { formatPrice } from '../../locales';
import { useAppSelector } from '../../redux';

export type QuoteSendModalProps = {
    show: boolean;
    quote: Quote;
    onClose: () => void;
    onSent: (quote: Quote) => void;
};

function parseEmails(value: string): string[] {
    return value
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(Boolean);
}

function formatSenderSignature(user?: { firstname?: string; lastname?: string } | null) {
    const firstname = String(user?.firstname || '').trim();
    const lastname = String(user?.lastname || '').trim().toUpperCase();
    const name = [firstname, lastname].filter(Boolean).join(' ');
    return name ? `${name}.` : '';
}

function QuoteSendModal(props: QuoteSendModalProps) {
    const { show, quote, onClose, onSent } = props;
    const { t } = useTranslation();
    const { user } = useAppSelector((state) => state.auth);

    const options = useMemo(() => quoteRecipientOptions(quote.client), [quote.client]);
    const defaultTo = useMemo(() => defaultQuoteRecipientEmail(quote.client), [quote.client]);
    const readOnly = !quote.isOpenForClientDecision();
    const hasRemovable = (quote.items || []).some((item) => !!item.removable && !item.quantityMode);
    const hasQuantityEditable = (quote.items || []).some((item) => {
        if (!item.quantityMode)
            return false;
        const min = Number(item.quantityMin);
        const max = Number(item.quantityMax);
        return Number.isFinite(min) && Number.isFinite(max) && min < max;
    });
    const projectName = quote.project?.name || '';
    const senderSignature = formatSenderSignature(user);
    // Must match helpers/quote_public.buildEmailHtml (French, server-side).
    const ctaLabel = readOnly ? 'Voir le devis' : 'Voir et accepter le devis';

    const [to, setTo] = useState(defaultTo);
    const [cc, setCc] = useState('');
    const [reminder, setReminder] = useState(false);
    const [subject, setSubject] = useState(quote.defaultSendSubject(false));
    const [personalMessage, setPersonalMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show)
            return;
        setTo(defaultTo);
        setCc('');
        setReminder(false);
        setSubject(quote.defaultSendSubject(false));
        setPersonalMessage('');
        setError('');
        setSending(false);
    }, [show, defaultTo, quote]);

    const onToggleReminder = (checked: boolean) => {
        setReminder(checked);
        const base = `Devis ${quote.slug} — ${quote.name}`;
        const reminderSubject = `Relance — ${base}`;
        // Only auto-adjust subject when it still matches the previous default.
        if (checked && (subject === base || !subject.trim()))
            setSubject(reminderSubject);
        else if (!checked && subject === reminderSubject)
            setSubject(base);
    };

    const addRecipient = (email: string, target: 'to' | 'cc') => {
        const list = parseEmails(target === 'to' ? to : cc);
        if (list.some((item) => item.toLowerCase() === email.toLowerCase()))
            return;
        const next = [...list, email].join(', ');
        if (target === 'to')
            setTo(next);
        else
            setCc(next);
    };

    const submit = async () => {
        const toEmails = parseEmails(to);
        if (!toEmails.length) {
            setError(String(t('quotes.send_missing_to') || ''));
            return;
        }

        setSending(true);
        setError('');
        try {
            const json = await QuotesApi.send(quote._id, {
                to: toEmails,
                cc: parseEmails(cc),
                subject,
                personalMessage,
                reminder,
            });
            onSent(json.quote);
            onClose();
        } catch (err: any) {
            setError(err?.message || String(t('quotes.send_failed') || ''));
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal show={show} onClose={onClose} size="2xl">
            <Modal.Header>
                {quote.hasBeenSent() ? t('quotes.resend_email') : t('quotes.send_email')}
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_to')}</label>
                        <input
                            type="text"
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder="email@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_cc')}</label>
                        <input
                            type="text"
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                            value={cc}
                            onChange={(e) => setCc(e.target.value)}
                        />
                    </div>
                    {options.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {options.map((option) => (
                                <button
                                    key={option.email}
                                    type="button"
                                    className="inline-flex items-center gap-1 text-xs border rounded-full px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    onClick={() => addRecipient(option.email, 'to')}
                                >
                                    <span>{option.label}</span>
                                    {option.role && (
                                        <Badge color={roleBadgeColor(option.role)} size="xs">
                                            {t(roleLabelKey(option.role)) || option.role}
                                        </Badge>
                                    )}
                                    {option.isPrimary && (
                                        <Badge color="gray" size="xs">{t('quotes.send_primary')}</Badge>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 dark:border-gray-600 px-3 py-2">
                        <div>
                            <div className="text-sm font-medium">{t('quotes.send_reminder')}</div>
                            <div className="text-xs text-gray-500">{t('quotes.send_reminder_help')}</div>
                        </div>
                        <ToggleSwitch label="" checked={reminder} onChange={onToggleReminder} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_subject')}</label>
                        <input
                            type="text"
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_preview')}</label>
                        {/* Preview mirrors server buildEmailHtml (always French). */}
                        <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-4 space-y-3 text-sm text-gray-800 dark:text-gray-100">
                            <p>Bonjour,</p>
                            <p>
                                Veuillez trouver ci-joint le devis <b>{quote.name}</b> (n° <b>{quote.slug}</b>)
                                {projectName ? <> pour le projet <b>{projectName}</b></> : null}
                                .
                            </p>
                            <p>
                                Montant total : <b>{formatPrice(quote.price || 0)}</b>
                            </p>
                            {hasRemovable && !readOnly && (
                                <p>
                                    Certaines lignes de ce devis sont optionnelles et peuvent être retirées avant acceptation.
                                </p>
                            )}
                            {hasQuantityEditable && !readOnly && (
                                <p>
                                    Certaines lignes permettent d&apos;ajuster la quantité avant acceptation.
                                </p>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                    {t('quotes.send_message')}
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800"
                                    rows={3}
                                    value={personalMessage}
                                    onChange={(e) => setPersonalMessage(e.target.value)}
                                    placeholder={t('quotes.send_message_placeholder') || ''}
                                />
                            </div>

                            <div>
                                <span className="inline-block px-4 py-2 rounded-md bg-blue-600 text-white font-semibold pointer-events-none select-none">
                                    {ctaLabel}
                                </span>
                                <p className="mt-1 text-xs text-gray-500">{t('quotes.send_cta_readonly')}</p>
                            </div>

                            <p>Le PDF du devis est également joint à cet e-mail.</p>
                            <div className="space-y-0">
                                <p>Cordialement,</p>
                                {senderSignature && <p>{senderSignature}</p>}
                            </div>
                        </div>
                    </div>

                    <p className="text-sm text-gray-500">
                        {t('quotes.send_pdf_note')}
                    </p>
                    {error && (
                        <p className="text-sm text-red-600">{error}</p>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button title={t('common.cancel')} color="secondary" onClick={onClose} />
                <Button
                    title={quote.hasBeenSent() ? t('quotes.resend_email') : t('quotes.send_email')}
                    color="primary"
                    onClick={submit}
                    loading={sending}
                />
            </Modal.Footer>
        </Modal>
    );
}

export default QuoteSendModal;
